import { BP_ORDERED, BP_NAME } from "./breakpoint";
import { computed, ref } from "vue";
import { Emitter } from "Util/emitter";

const breakpoints = BP_ORDERED.map((bp) => {
    return {
        name: bp.name,
        watcher: window.matchMedia(`(min-width: ${bp.minWidth}px)`)
    };
});

const bpRefs = {};
breakpoints.forEach((bp) => {
    bpRefs[bp.name] = ref(bp.watcher.matches);
});

// Add a change handler that flips the boolean value of each
// data[breakpoint] when its respective media query
// indicates there has been a change.
breakpoints.forEach((bp) => {
    const listener = function () {
        bpRefs[bp.name].value = bp.watcher.matches;
    };
    bp.watcher.addEventListener("change", listener);
});

const breakpoint = computed(() => {
    for (let i = BP_NAME.length - 1; i >= 0; i--) {
        const bpName = BP_NAME[i];
        if (bpRefs[bpName].value) {
            return bpName;
        }
    }

    // return first (smallest) breakpoint name
    return BP_NAME[0];
});

// Add a throttled event handler for the resize event that
// updates the globalWidth property of the responsive manager.
// pc-viewport components depend on the globalWidth property
// to update their widths.
const emitter = new Emitter();
const globalWidth = ref(window.innerWidth);
let resizing = false;
window.addEventListener("resize", function () {
    function resizeCallback() {
        globalWidth.value = window.innerWidth;
        resizing = false;
        emitter.emit("resize");
    }

    if (!resizing) {
        resizing = true;

        if (window.requestAnimationFrame) {
            window.requestAnimationFrame(resizeCallback);
        } else {
            setTimeout(resizeCallback, 66);
        }
    }
});

export const responsiveManager = {
    ...bpRefs,
    breakpoint,
    globalWidth,
    addResizeListener(callback) {
        emitter.on("resize", callback);
    },
    removeResizeListener(callback) {
        emitter.off("resize", callback);
    }
};

/**
 * Utility function for determining if the calling component should behave as
 * though it is at or above the responsive breakpoint specified. If the viewport
 * passed in is defined, that will be used as the responsive context. Otherwise,
 * the global responsiveManager will be used.
 *
 * @param {*} viewport
 * @param {*} breakpoint
 */
export function isAtLeast(viewport, breakpoint) {
    if (viewport != null) {
        return viewport[breakpoint.name];
    } else {
        return responsiveManager[breakpoint.name].value;
    }
}

/**
    Convert a viewport-compatible responsive config of the form
    {
        elt: {
            class1: {xs: "xs-behavior", md: "md-behavior"},
            class2: {xs: "xs-behavior", lg: "lg-behavior"}
        },
        elt2: {
            class3: {sm: "", md: "md-behavior" }
        }
    }

    to a map of the form (at bp == 'sm')
    {
        elt: "class1-xs-behavior class2-xs-behavior"
        elt2: "class3"
    }

    such that it can easily be consumed by the calling component's markup.

    A key of 'always' will cause the string value to be appended to the
    resulting class string regardless of the calculated viewport. This
    is usually unnecessary since a static class string can be used in
    addition to the computed class map, but can be used by places like the
    responsiveHelpers in _elementBase to avoid having to add a static class
    in all the places consuming the helpers.

    {
        elt: {
            class1: { xs: "a", md: "b" }
            always: "class2 class3-c"
        }
    }

    At breakpoint lg =>

    "class1-b class2 class3-c"
*/
export function getResponsiveClassMap(viewport, config) {
    const outputMap = {};
    for (const eltKey in config) {
        if (config.hasOwnProperty(eltKey)) {
            const eltConfig = config[eltKey];
            outputMap[eltKey] = getResponsiveClasses(viewport, eltConfig);
        }
    }
    return outputMap;
}

// Utility function for getting a string of responsive classes for a single element.
// The appropriate responsive classes will be determined either based on the breakpoint
// of the calling component's viewport, if defined, or otherwise by the breakpoint
// of the global responsive manager.
export function getResponsiveClasses(viewport, eltConfig) {
    if (viewport != null) {
        return _getResponsiveClassesForContext(eltConfig, viewport.bp);
    } else {
        return _getResponsiveClassesForContext(eltConfig, responsiveManager.breakpoint.value);
    }
}

// Return a list of all breakpoints smaller than and including the
// context breakpoint. This may be a global responsiveManager breakpoint
// or a viewport's breakpoint.
function _getQualifyingBps(contextBreakpoint) {
    for (let i = BP_NAME.length - 1; i >= 0; i--) {
        if (contextBreakpoint === BP_NAME[i]) {
            return BP_NAME.slice(0, i + 1);
        }
    }
    return BP_NAME.slice(0, 1);
}

// Return the appropriate responsive classes given the config and the current context
// breakpoint. The contextBreakpoint may be either a global responsiveManager
// breakpoint or a viewport's breakpoint.
function _getResponsiveClassesForContext(eltConfig, contextBreakpoint) {
    const classStrs = [];
    for (const classKey in eltConfig) {
        if (eltConfig.hasOwnProperty(classKey)) {
            // The config for a given base class for this element
            const eltClassConfig = eltConfig[classKey];

            // If the class key is the special key "always", just return the value which
            // should be a space-separated string of classes that we want to include at every
            // breakpoint.
            if (classKey === "always") {
                classStrs.push(eltClassConfig);
            }

            // Calculate the responsive class based on the config and the current viewport
            // breakpoint. We calculate the possible config sections to check based on the
            // effective bp of the viewport, then check whether those sections are
            // defined in the config in descending order, and compose a class based on the
            // first one we find.
            // For example, if the viewport bp is "sm", and the config has defined behavior
            // for "xs", "md", and "xl", we will return a class corresponding to the
            // config defined for the "xs" breakpoint, because the viewport doesn't
            // qualify for either of the larger configs.

            // If the value at a breakpoint is the empty string, then the returned class will
            // be just the base class
            const qualifiedBps = _getQualifyingBps(contextBreakpoint).reverse();
            for (const bp in qualifiedBps) {
                if (eltClassConfig.hasOwnProperty(qualifiedBps[bp])) {
                    const configValStr =
                        eltClassConfig[qualifiedBps[bp]] !== "" ? `-${eltClassConfig[qualifiedBps[bp]]}` : "";
                    classStrs.push(`${classKey}${configValStr}`);
                    break;
                }
            }
        }
    }
    return classStrs.join(" ");
}
